/*
 * @providesModule SaveAccountFlowController
 *
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Alert, Platform, StatusBar} from 'react-native';
import {SIGN_UP_SUBSCRIBE_NEWSLETTERS} from 'AWSConstants';
import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import DateUtils from 'DateUtils';
import EmailVerify from 'EmailVerify';
import ErrorDialogModal from 'ErrorDialogModal';
import FraudPromptNavigator from 'FraudPromptNavigator';
import I18n from 'INTL';
import LoadingIndicator from 'LoadingIndicator';
import ModalNavigationController, {ModalProps} from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import PhoneVerify, {getHeaderCopyDefault} from 'PhoneVerify';
import React from 'react';
import SKZCloudGaming from 'SKZCloudGaming';
import SKZConstants from 'SKZConstants';
import SMSVerifyController from 'SMSVerifyController';
import Tournament from 'SKZTournament';
import {SKZUserInterface} from 'SKZUser';
import SignUpView from 'SignUpView';
import AgeVerificationView from 'AgeVerificationView';
import StayInLoopView from 'StayInLoopView';
import SignupViewLegacy from 'SignupViewLegacy';
import AuthenticationController from 'AuthenticationController';
import _ from 'lodash';
import SKZPersonalInfo from 'SKZPersonalInfo';
import {SaveAccountProps, AgeVerifyProps, EmailVerifyProps, PhoneVerifyProps} from './types';
import {pathFromTypes} from 'PhoneVerify';
import SkillzStyle from 'SkillzStyle';
const DUPLICATE_EMAIL_ERROR_KEY = 'Another account is already using this email address';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import {getCohort} from '../../UnifiedLogin/Utility/utils';

/*
  ageVerifyProps is a dictionary that tells the AgeVerify how to render and what minimumAge to use.  It uses the following structure:
  {
    minimumAge: number, // Required
    titleText: string, // Required. Translate this as you pass it in
    subTitleText: string, // Optional. Translate this as you pass it in
    navigator: string // Optional
  }

  saveAccountProps is a dictionary that tells the SaveAccount how to render.  It uses the following structure:
  {
    shouldCheckEmail: boolean, // Required
    tournament: SKZTournament,
    inPurchaseVCFlow: boolean,
    shouldShowNavigation: boolean,
    userDeposit: boolean,
    shouldShowOTANavBar: boolean,
    navigator: string // Optional
    isAfterSubmitScore: boolean, // Optional
    onAccountSaveTrackingEvent: string // Optional
  }

  emailVerifyProps is a dictionary that tells the SaveAccount how to render.  It uses the following structure:
  {
    shouldCheckEmailVerify: boolean, // Required
    shouldShowOTANavBar: boolean,
    navigator: string, // Optional
    disableCompletionNavigation: boolean, // Optional. If true, email verify will not pop when complete (but will still pop on back). Screen transition should be handled in completion if true.
    onSendEmailTrackingEvent: string // Optional
  }

  phoneVerifyProps is a dictionary that tells the SaveAccount how to render.  It uses the following structure:
  {
    shouldCheckPhoneVerify: boolean, // Required
    shouldShowOTANavBar: boolean,
  }
*/

const SaveAccountFlowController = {
  cloudSaveAccount(completion: (success?: boolean) => {}) {
    const TOPIC_WEB_ACCOUNT_START = 'WEB_SAVE_ACCOUNT_START';
    const TOPIC_WEB_ACCOUNT_END = 'WEB_SAVE_ACCOUNT_END';
    const TOPIC_CALLER = 'SaveAccountFlowController';
    const USER_CANCELLED = 'cancelled';
    console.debug('Cloud Gaming: web save account');

    //show loading while going through
    LoadingIndicator.show();

    //subscribe to any message coming back
    SKZCloudGaming.subscribeMessaging(TOPIC_WEB_ACCOUNT_END, TOPIC_CALLER, (jsonObject: string) => {
      LoadingIndicator.hide();
      const data = JSON.parse(jsonObject);
      if (data.success && data.result) {
        // grab new account info / determine if account was saved or not true = account saved false = account not saved
        CurrentUserStore.fetchUser().then((userInfo: SKZUserInterface) => {
          if (userInfo && !CurrentUserStore.isSavedAccount()) {
            //account not saved
            completion(false);
          } else {
            completion(true);
          }
        });
      } else {
        //error state
        if (data.result != USER_CANCELLED) {
          this._showGenericErrorModal(
            data.result,
            () => {
              this.cloudSaveAccount(completion);
            },
            typeof data.result === 'string' ? {message: data.result} : {}
          );
        } else {
          //account not saved
          completion(false);
        }
      }
    });

    //send a message to trigger the web account save flow
    SKZCloudGaming.sendMessageCloud(
      TOPIC_WEB_ACCOUNT_START,
      {},
      (error: string, success: boolean) => {
        if (error || !success) {
          console.warn('WEB_SAVE_ACCOUNT_START_ERROR');
          SKZCloudGaming.unsubscribeMessaging(TOPIC_WEB_ACCOUNT_END, TOPIC_CALLER, () => {
            LoadingIndicator.hide();
          });
        }
      }
    );

    return;
  },

  async displaySaveAccountFlowWithUserInfoAndPhoneVerify(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps,
    userPersonalInfo: SKZPersonalInfo,
    userInfo: Object,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => void
  ) {
    const shouldShowCloud = tournament ? tournament.isCashTournament() : true;
    if (
      SKZCloudGaming.getConstants().isCloudGaming &&
      SKZCloudGaming.getConstants().webSaveAccountEnabled !== false &&
      !CurrentUserStore.isSavedAccount() &&
      shouldShowCloud
    ) {
      this.cloudSaveAccount(completion);
    } else {
      if (
        this._checkAgeVerified(
          ageVerifyProps,
          saveAccountProps,
          emailVerifyProps,
          phoneVerifyProps,
          userPersonalInfo,
          tournament,
          popToRoot,
          completion
        )
      ) {
        if (
          ClientPropertyConstants.getFTUEV2FeatureEnabled() &&
          ClientPropertyConstants.getSkipCashGameFullRegCheck() &&
          tournament?.isCashTournament()
        ) {
          completion(true);
        } else {
          if (
            saveAccountProps.shouldCheckEmail &&
            ((tournament &&
              !tournament.shouldRegisterBeforePlay() &&
              !saveAccountProps.isAfterSubmitScore) ||
              (await this._checkUserVerified(
                ageVerifyProps,
                saveAccountProps,
                emailVerifyProps,
                phoneVerifyProps,
                tournament,
                popToRoot,
                completion
              )))
          ) {
            if (
              this._checkUserEmailVerified(
                ageVerifyProps,
                saveAccountProps,
                emailVerifyProps,
                userInfo,
                tournament,
                popToRoot,
                completion
              )
            ) {
              this._displayEmailVerify(
                ageVerifyProps,
                saveAccountProps,
                emailVerifyProps,
                phoneVerifyProps,
                userInfo,
                tournament,
                popToRoot,
                completion
              );
            } else if (
              this._checkUserPhoneVerified(phoneVerifyProps, userPersonalInfo, tournament)
            ) {
              this._checkPhoneVerifiedCash().then((checkPhone) => {
                if (
                  ClientPropertyConstants.getVerifySMSCloseButton() &&
                  tournament?.isCashTournament() &&
                  !checkPhone
                ) {
                  completion(true);
                } else {
                  this._displayPhoneVerify(userPersonalInfo, completion, tournament);
                }
              });
            } else {
              completion(true);
            }
          }
        }
      }
    }
  },

  displaySaveAccountFlowWithUserInfo(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    userPersonalInfo: SKZPersonalInfo,
    userInfo: Object,
    tournament: Tournament | null,
    popToRoot: boolean,
    completion: (success: boolean) => void
  ) {
    this.displaySaveAccountFlowWithUserInfoAndPhoneVerify(
      ageVerifyProps,
      saveAccountProps,
      emailVerifyProps,
      null,
      userPersonalInfo,
      userInfo,
      tournament,
      popToRoot,
      completion
    );
  },

  async displaySaveAccountFlow(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps | null,
    tournament: Tournament | null,
    popToRoot: boolean,
    completion: (success: boolean) => void,
    useCache: boolean = true,
    matchId: string = ''
  ) {
    LoadingIndicator.show();
    const userPersonalInfo = await CurrentUserStore.fetchUserPersonalInfo();
    const userInfo = await CurrentUserStore.fetchUser(useCache);
    LoadingIndicator.hide();
    this.displaySaveAccountFlowWithUserInfoAndPhoneVerify(
      ageVerifyProps,
      saveAccountProps,
      emailVerifyProps,
      phoneVerifyProps,
      userPersonalInfo,
      userInfo,
      tournament,
      popToRoot,
      completion
    );
  },

  _checkAgeVerified(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps,
    userPersonalInfo: SKZPersonalInfo,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => {}
  ): boolean {
    const ageWithin = DateUtils.ageWithin(userPersonalInfo, ageVerifyProps);
    if (tournament) {
      if (
        !(
          (tournament.isPointsTournament() || tournament.isZEntryBonusCashPrizeTournament()) &&
          !tournament.isFriendTournament()
        ) &&
        !ageWithin
      ) {
        this._displayAgeVerification(
          ageVerifyProps,
          saveAccountProps,
          emailVerifyProps,
          phoneVerifyProps,
          tournament,
          popToRoot,
          completion
        );
        return false;
      } else {
        return true;
      }
    } else if (!ageWithin) {
      this._displayAgeVerification(
        ageVerifyProps,
        saveAccountProps,
        emailVerifyProps,
        phoneVerifyProps,
        tournament,
        popToRoot,
        completion
      );
      return false;
    } else {
      return true;
    }
  },

  _displayAgeVerification(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => {}
  ) {
    const showStatusBar = emailVerifyProps.isShowingAndroidStatusBar;
    if (Platform.OS === 'android' && showStatusBar) {
      StatusBar.setHidden(true);
    }

    const navigator =
      ageVerifyProps.navigator == SKZConstants.FRAUD_NAVIGATOR
        ? FraudPromptNavigator
        : NavigationManager;
    const ageView = (
      <AgeVerificationView
        minimumAge={ageVerifyProps.minimumAge}
        preCheck={!ClientPropertyConstants.getIsGDPREnabled()}
        onClosePress={() => {
          trackTelemetryEvent({
            event: TelemetryEventName.AGE_INPUT_SCREEN_CTA_CLICK,
            payload: {
              cta: 'close',
            },
          });
          completion(false);
          if (Platform.OS === 'android' && showStatusBar) {
            StatusBar.setHidden(false);
          }
          NavigationManager.pop();
        }}
        onNextPress={(
          dateToSubmit: string,
          isConsent: boolean,
          onSuccess: () => void,
          onError: () => void
        ) => {
          LoadingIndicator.show();
          if (Platform.OS === 'android' && showStatusBar) {
            StatusBar.setHidden(false);
          }
          CurrentUserStore.updateUserPersonalInfo({birth_date: dateToSubmit})
            .then(() => {
              ageVerifyProps.didDisplayAgeVerification = true;
              // Dismiss Age Verification popup right after they press Next in Android alone on EventDetailsScreen
              if (Platform.OS === 'android' && saveAccountProps.isFromLiveEventScreen) {
                NavigationManager.pop();
              }
              this.displaySaveAccountFlow(
                ageVerifyProps,
                saveAccountProps,
                emailVerifyProps,
                phoneVerifyProps,
                tournament,
                popToRoot,
                completion
              );
              onSuccess();
            })
            .catch((error: any) => {
              const uhOhError = I18n.message(
                "Uh oh! Doesn't look like that worked. Please try again."
              );
              Alert.alert('', uhOhError, [{text: I18n.message('OK')}]);
              console.warn('AgeVerificationScreen: Error saving date of birth:', error);
              onError();
            })
            .finally(() => {
              LoadingIndicator.hide();
            });
        }}
      />
    );
    navigator.pushComponentWithScene(ageView, true, 'AgeVerificationView');
  },

  async _checkUserVerified(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => void
  ): Promise<boolean> {
    if (tournament) {
      if (
        !(tournament.isPointsTournament() && !tournament.isFriendTournament()) &&
        !CurrentUserStore.isSavedAccount()
      ) {
        this._displaySaveAccount(
          ageVerifyProps,
          saveAccountProps,
          emailVerifyProps,
          phoneVerifyProps,
          tournament,
          popToRoot,
          completion
        );
        return false;
      } else {
        return true;
      }
    } else if (!CurrentUserStore.isSavedAccount()) {
      this._displaySaveAccount(
        ageVerifyProps,
        saveAccountProps,
        emailVerifyProps,
        phoneVerifyProps,
        tournament,
        popToRoot,
        completion
      );
      return false;
    } else {
      return true;
    }
  },

  _checkUserEmailVerified(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    userInfo: SKZPersonalInfo,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => {}
  ): boolean {
    if (tournament && tournament.isFriendTournament() && !userInfo.email_verified) {
      return true;
    } else if (emailVerifyProps.shouldCheckEmailVerify && !userInfo.email_verified) {
      return true;
    } else {
      return false;
    }
  },

  _displayEmailVerify(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps,
    userInfo: Object,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => {}
  ) {
    const navigator =
      emailVerifyProps.navigator == SKZConstants.FRAUD_NAVIGATOR
        ? FraudPromptNavigator
        : NavigationManager;
    const scene = (
      <EmailVerify
        navigator={navigator.getNavigator()}
        shouldShowOTANavBar={emailVerifyProps.shouldShowOTANavBar}
        isShowingAndroidStatusBar={emailVerifyProps.isShowingAndroidStatusBar}
        prohibitDisableKeyboardManager={emailVerifyProps.prohibitDisableKeyboardManager}
        onBackButtonPress={() => {
          completion(false);
        }}
        onDone={() => {
          completion(true);
          if (
            !tournament &&
            !this._checkUserPhoneVerified(phoneVerifyProps, userInfo, tournament)
          ) {
            // need to check if theres no tournaments and no phone verify so that we maintain the pop
            if (!emailVerifyProps.disableCompletionNavigation) {
              navigator?.getNavigator()?.pop();
            }
          } else {
            this.displaySaveAccountFlow(
              ageVerifyProps,
              saveAccountProps,
              emailVerifyProps,
              phoneVerifyProps,
              tournament,
              popToRoot,
              completion
            );
          }
        }}
        onSendEmailTrackingEvent={emailVerifyProps.onSendEmailTrackingEvent}
      />
    );

    if (emailVerifyProps.didDisplaySaveAccount) {
      navigator.replaceComponent(scene, 'Email Verify');
    } else {
      navigator.pushComponentWithScene(scene, true, 'Email Verify');
    }
  },

  _checkUserPhoneVerified(
    phoneVerifyProps: PhoneVerifyProps,
    userPersonalInfo: SKZPersonalInfo,
    tournament: Tournament
  ): boolean {
    const userNotVerified = !userPersonalInfo?.phone_verified;
    const friendsMatchCheck =
      tournament?.isFriendTournament() &&
      ClientPropertyConstants.getFriendsTournamentsPhoneNumber();
    const cashMatchCheck =
      tournament?.isCashTournament() &&
      !tournament.isZEntryBonusCashPrizeTournament() &&
      ClientPropertyConstants.getVerifySMSGameplay();
    const isNonTournamentOrValidTournament = !tournament || friendsMatchCheck || cashMatchCheck;
    const userShouldVerify = phoneVerifyProps?.shouldCheckPhoneVerify;

    if (userShouldVerify && userNotVerified && isNonTournamentOrValidTournament) {
      return true;
    }
    return false;
  },

  async _checkPhoneVerifiedCash(): boolean {
    const SMSVerifyTime = 'SMSVerifyTime';
    const currentTime = new Date();
    try {
      const storedTime = await AsyncStorage.getItem(SMSVerifyTime);
      const parsedTime = storedTime ? new Date(storedTime) : null;
      if (
        !storedTime ||
        !(
          parsedTime?.getDay() === currentTime?.getDay() &&
          parsedTime?.getMonth() === currentTime?.getMonth()
        )
      ) {
        try {
          await AsyncStorage.setItem(SMSVerifyTime, `${currentTime}`);
          return true;
        } catch (error) {
          return false;
          // Error saving data
        }
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  },

  _displayPhoneVerify(
    userPersonalInfo: SKZPersonalInfo,
    completion: (success: boolean) => {},
    tournament: Tournament
  ) {
    const verifyModalProps = {
      animationType: 'fade',
      transparent: true,
    } as ModalProps;

    ModalNavigationController.showModal(
      <PhoneVerify
        userPersonalInfo={userPersonalInfo}
        onDone={(didSucceed: boolean) => {
          completion(didSucceed);
          ModalNavigationController.hideModal();
        }}
        headerCopyOverride={tournament?.isFriendTournament() ? null : getHeaderCopyDefault()}
        pathFrom={tournament?.isCashTournament() ? pathFromTypes.cashPlay : null}
      />,
      verifyModalProps
    );
  },

  _renderEmailSignUp(
    navigator: typeof NavigationManager | typeof FraudPromptNavigator,
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => {},
    email?: string,
    allowExit?: boolean,
    allowSignIn?: boolean
  ) {
    const emailSignup = (
      <SignupViewLegacy
        email={email}
        firstDeposit={saveAccountProps.firstDeposit}
        isAfterSubmitScore={saveAccountProps.isAfterSubmitScore ?? false}
        onClosePress={() => {
          completion(false);
          navigator.pop();
          if (Platform.OS === 'android' && emailVerifyProps.isShowingAndroidStatusBar) {
            StatusBar.setHidden(false);
          }
        }}
        signInPress={() => {
          AuthenticationController.displaySignInScreen(false);
        }}
        completion={() => {
          if (Platform.OS === 'android' && emailVerifyProps.isShowingAndroidStatusBar) {
            StatusBar.setHidden(false);
          }
          if (
            !emailVerifyProps.shouldCheckEmailVerify &&
            (!tournament ||
              tournament.synchronous ||
              (tournament.live_event && tournament.bracket)) &&
            !popToRoot &&
            !saveAccountProps.isAfterSubmitScore
          ) {
            navigator?.getNavigator()?.pop();
          }
          emailVerifyProps.didDisplaySaveAccount = true;

          this.displaySaveAccountFlow(
            ageVerifyProps,
            saveAccountProps,
            emailVerifyProps,
            phoneVerifyProps,
            tournament,
            popToRoot,
            completion,
            false
          );
        }}
        navigator={navigator}
        onBackButtonPress={saveAccountProps.onBackButtonPress}
        allowExit={
          allowExit ?? saveAccountProps.allowExit ?? saveAccountProps.shouldShowNavigation ?? false
        }
        allowSignIn={
          allowSignIn ??
          saveAccountProps.allowSignIn ??
          saveAccountProps.shouldShowNavigation ??
          false
        }
        tournament={saveAccountProps.tournament}
        onContinueWithIDPPress={AuthenticationController.signUpUserWithIDP.bind(
          AuthenticationController
        )}
        onSignUpPress={AuthenticationController.saveUserWithUsername.bind(AuthenticationController)}
        notifySDK={AuthenticationController.notifySDKHasLoaded.bind(AuthenticationController)}
      />
    );
    return emailSignup;
  },

  _getIsNewUI(): boolean {
    return ClientPropertyConstants.getFTUEV2FeatureEnabled();
  },

  _renderSMSSignUpView(
    navigator: typeof NavigationManager | typeof FraudPromptNavigator,
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => {}
  ) {
    const confirmVerification = (
      code: string,
      phoneNumber: string,
      onSuccess: () => void,
      onFailure: (error: string) => void
    ) => {
      SMSVerifyController.customConfirmVerification(
        code,
        phoneNumber,
        onSuccess,
        onFailure,
        (): void => {
          // Stay in loop view is not required in the revamp FTUE
          if (this._getIsNewUI()) {
            NavigationManager.popToRoot();
          } else {
            NavigationManager.replaceComponent(
              <StayInLoopView
                hideCloseButton={true}
                onContinueWithIDPPress={AuthenticationController.signUpUserWithIDP.bind(
                  AuthenticationController
                )}
                onClosePress={() => {
                  trackTelemetryEvent({
                    event: TelemetryEventName.EMAIL_INPUT_PAGE_CLICKED,
                    includeUserWalletDetails: true,
                    payload: {
                      email_ID: '',
                      cta: 'Close',
                      source: 'Menu',
                    },
                  });
                  NavigationManager.popNavigator();
                }}
                handleNextPress={async (userName: string, isConsent: boolean) => {
                  AWSManager.trackEvent(SIGN_UP_SUBSCRIBE_NEWSLETTERS, {result: Number(isConsent)});
                  CurrentUserStore.updateUser({email_opt_in: isConsent});
                  try {
                    await CurrentUserStore.updateUserPersonalInfoWithCurrentData({email: userName});
                    const scene = this._renderEmailSignUp(
                      navigator,
                      ageVerifyProps,
                      saveAccountProps,
                      emailVerifyProps,
                      phoneVerifyProps,
                      tournament,
                      popToRoot,
                      completion,
                      userName,
                      false,
                      false
                    );
                    trackTelemetryEvent({
                      event: TelemetryEventName.EMAIL_INPUT_PAGE_CLICKED,
                      includeUserWalletDetails: true,
                      payload: {
                        email_ID: userName,
                        cta: 'Next',
                        source: 'Menu',
                        ...(ClientPropertyConstants.isFTUEv3Enabled() && {
                          cohort: getCohort(),
                        }),
                        toggle: isConsent,
                        success: true,
                      },
                    });
                    navigator.pushComponentWithScene(scene, true, 'SaveAccount');
                    return 'success';
                  } catch (emailUpdateError: any) {
                    if (emailUpdateError && emailUpdateError.includes(DUPLICATE_EMAIL_ERROR_KEY)) {
                      AuthenticationController.displaySignInScreen(false, undefined, {
                        email: userName,
                      });
                    }
                    trackTelemetryEvent({
                      event: TelemetryEventName.EMAIL_INPUT_PAGE_CLICKED,
                      includeUserWalletDetails: true,
                      payload: {
                        email_ID: userName,
                        cta: 'Next',
                        source: 'Menu',
                        ...(ClientPropertyConstants.isFTUEv3Enabled() && {
                          cohort: getCohort(),
                        }),
                        toggle: isConsent,
                        success: false,
                      },
                    });

                    throw emailUpdateError;
                  }
                }}
                handleNextPressWithoutConsent={() => {
                  const delayToExitFlow = 1000;
                  setTimeout(() => {
                    completion(false);
                    NavigationManager.popNavigator();
                  }, delayToExitFlow);
                }}
              />,
              'StayInLoopView'
            );
          }
        }
      );
    };

    const smsSignup = (
      <SignUpView
        allowExit={saveAccountProps.allowExit ?? saveAccountProps.shouldShowNavigation ?? false}
        isAfterSubmitScore={saveAccountProps.isAfterSubmitScore ?? false}
        SMSProps={{
          isValidNumber: SMSVerifyController.isValidNumber,
          sendSMSVerification: SMSVerifyController.requestVerificationSignup,
          fetchCountry: SMSVerifyController.fetchCountry,
          formatNumber: SMSVerifyController.formatNumber,
          getCountryCode: SMSVerifyController.getCountryCode,
          getFlag: SMSVerifyController.getFlag,
          countryData: SMSVerifyController.getCountryData(),
          numberOfInputs: 5,
          codeValidation: confirmVerification,
          onPhoneNumberInUse: (inputPhoneNumber: string, country: string, countryValue: string) => {
            AuthenticationController.displaySignInScreen(false, {
              phoneNumber: inputPhoneNumber,
              country: country,
              countryValue: countryValue,
              userAlreadyHaveAccount: true,
            });
          },
        }}
        onClosePress={() => {
          completion(false);
          navigator.pop();
          if (Platform.OS === 'android' && emailVerifyProps.isShowingAndroidStatusBar) {
            StatusBar.setHidden(false);
          }
        }}
        trackEvent={AWSManager.trackEvent}
        onBackButtonPress={saveAccountProps.onBackButtonPress}
        onSignInPress={() => {
          AuthenticationController.displaySignInScreen(false);
        }}
        isNewUI={this._getIsNewUI()}
        gameTheme={SkillzStyle.getOverriddenFallbackTheme()}
        onContinueWithIDPPress={(fedtype, onDone, tournament, navigator) =>
          AuthenticationController._onContinuePressWithIDP({
            autoMerge: undefined,
            fedType: fedtype,
            onDone,
            tournament,
            navigator,
          })
        }
        onSignInWithEmailOTP={AuthenticationController.onSignInWithEmailOTP}
        onSignUpPress={AuthenticationController.saveUserWithUsername.bind(AuthenticationController)}
        completion={() => {
          if (Platform.OS === 'android' && emailVerifyProps.isShowingAndroidStatusBar) {
            StatusBar.setHidden(false);
          }
          if (
            !emailVerifyProps.shouldCheckEmailVerify &&
            (!tournament ||
              tournament.synchronous ||
              (tournament.live_event && tournament.bracket)) &&
            !popToRoot &&
            !saveAccountProps.isAfterSubmitScore
          ) {
            navigator?.getNavigator()?.pop();
          }
          emailVerifyProps.didDisplaySaveAccount = true;

          this.displaySaveAccountFlow(
            ageVerifyProps,
            saveAccountProps,
            emailVerifyProps,
            phoneVerifyProps,
            tournament,
            popToRoot,
            completion,
            false
          );
        }}
      />
    );
    return smsSignup;
  },

  _displaySaveAccount(
    ageVerifyProps: AgeVerifyProps,
    saveAccountProps: SaveAccountProps,
    emailVerifyProps: EmailVerifyProps,
    phoneVerifyProps: PhoneVerifyProps,
    tournament: Tournament,
    popToRoot: boolean,
    completion: (success: boolean) => {}
  ) {
    if (saveAccountProps.onBackButtonPress == undefined) {
      saveAccountProps.onBackButtonPress = () => NavigationManager.pop();
    }

    const showStatusBar = emailVerifyProps.isShowingAndroidStatusBar;
    if (Platform.OS === 'android' && showStatusBar) {
      StatusBar.setHidden(true);
    }
    const navigator =
      saveAccountProps.navigator == SKZConstants.FRAUD_NAVIGATOR
        ? FraudPromptNavigator
        : NavigationManager;

    const scene =
      ClientPropertyConstants.getFTUEV2FeatureEnabled() ||
      ClientPropertyConstants.getSMSSaveAccount()
        ? this._renderSMSSignUpView(
            navigator,
            ageVerifyProps,
            saveAccountProps,
            emailVerifyProps,
            phoneVerifyProps,
            tournament,
            popToRoot,
            completion
          )
        : this._renderEmailSignUp(
            navigator,
            ageVerifyProps,
            saveAccountProps,
            emailVerifyProps,
            phoneVerifyProps,
            tournament,
            popToRoot,
            completion
          );

    if (ageVerifyProps.didDisplayAgeVerification) {
      navigator.replaceComponent(scene, 'SaveAccount');
    } else {
      navigator.pushComponentWithScene(scene, true, 'SaveAccount');
    }
  },

  _showGenericErrorModal(
    error: Object,
    retryCallback: () => void,
    errorDialogModalProps?: {message?: string}
  ) {
    LoadingIndicator.hide();
    const done = () => {
      ModalNavigationController.hideModal();
    };
    const errorModal = (
      <ErrorDialogModal
        onDone={done}
        retryCallback={retryCallback}
        error={error}
        {...errorDialogModalProps}
      />
    );

    ModalNavigationController.showModal(errorModal);
  },
};

export {SaveAccountFlowController as default};
